import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import WallFeed from "@/components/WallFeed";
import { StudentAccessCards } from "@/components/StudentAccessCards";
import { PhotosOfWeek } from "@/components/PhotosOfWeek";
import LeaderboardCard from "@/components/LeaderboardCard";
import ClassCardActions from "@/components/ClassCardActions";
import { useI18n } from "@/i18n/i18n";
import { formatDMY } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { CalendarCheck, Wallet, BookOpen, AlertTriangle, ExternalLink, Landmark, CheckCircle2 } from "lucide-react";
import { BANK_ACCOUNTS } from "@/lib/bankAccounts";

const bankLabelFor = (value) => {
  if (!value) return null;
  const b = BANK_ACCOUNTS.find((x) => x.value === value);
  return b ? b.label : value;
};

// Returns { text, tone } describing how soon the payment is due (today, in N days, overdue N days, etc.)
function describeDueDate(dueIso, t) {
  if (!dueIso) return { text: t("no_due_date"), tone: "neutral" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(dueIso + "T00:00:00");
  const diffMs = due - today;
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: t("overdue_by", { n: Math.abs(days) }), tone: "danger" };
  if (days === 0) return { text: t("due_today"), tone: "warn" };
  if (days === 1) return { text: t("due_tomorrow"), tone: "warn" };
  if (days <= 7) return { text: t("due_in_days", { n: days }), tone: "warn" };
  return { text: t("due_in_days", { n: days }), tone: "neutral" };
}

export default function StudentHome() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ groups: [], att: [], pay: [], sedes: [], programs: [], enrollments: [] });
  const [livePass, setLivePass] = useState(null);
  useEffect(() => {
    api.get("/live-pass/me").then(({ data: d }) => setLivePass(d.has_pass ? d : null)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      const [g, a, p, s, pr, en] = await Promise.all([
        api.get("/groups"), api.get("/attendance"), api.get("/payments"),
        api.get("/sedes"), api.get("/programs"), api.get("/enrollments"),
      ]);
      setData({ groups: g.data, att: a.data, pay: p.data, sedes: s.data, programs: pr.data, enrollments: en.data });
    })();
  }, []);

  const sedesMap = Object.fromEntries(data.sedes.map((x) => [x.id, x.name]));
  const programsMap = Object.fromEntries(data.programs.map((x) => [x.id, x.name]));
  const enrollmentsMap = Object.fromEntries(data.enrollments.map((x) => [x.id, x]));
  const pendingPay = data.pay.filter((p) => p.status === "pending");
  const totalPending = pendingPay.reduce((s, p) => s + Number(p.amount || 0), 0);

  // Sort by due_date asc (nulls last) and pick the most urgent
  const sortedPending = [...pendingPay].sort((a, b) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  });
  const nextPay = sortedPending[0];
  const nextEnr = nextPay?.enrollment_id ? enrollmentsMap[nextPay.enrollment_id] : null;
  const nextProgramName = nextPay?.program_id ? programsMap[nextPay.program_id] : null;
  const dueInfo = nextPay ? describeDueDate(nextPay.due_date, t) : null;

  const toneClasses = {
    danger: "from-red-50 via-orange-50 to-amber-50 border-red-200",
    warn: "from-amber-50 via-orange-50 to-yellow-50 border-amber-200",
    neutral: "from-blue-50 via-sky-50 to-cyan-50 border-blue-200",
  };
  const dueTextClasses = {
    danger: "bg-red-100 text-red-700",
    warn: "bg-amber-100 text-amber-800",
    neutral: "bg-blue-100 text-blue-700",
  };

  return (
    <div>
      <PageHeader title={`${t("welcome_back")}, ${user?.name?.split(" ")[0] || ""}`} subtitle={t("tagline")} testId="student-home-header" />

      {data.groups.length === 0 ? (
        /* Quien entró por WSS Live no está matriculado en ningún grupo: ve los
           mismos tres accesos, la línea de fotos y el muro. */
        livePass ? (
          <div className="space-y-12" data-testid="student-home-live-pass">
            <StudentAccessCards />
            <PhotosOfWeek />
            <WallFeed limit={1} allLink="/dashboard/wall-board" />
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
            {t("no_assignments")}
          </div>
        )
      ) : (
        <>
          {/* Next payment widget — only shown when there's a pending payment */}
          {nextPay && (
            <Card
              data-testid="next-payment-widget"
              className={`p-5 mb-6 bg-gradient-to-r border ${toneClasses[dueInfo.tone]} relative overflow-hidden`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2.5 rounded-full bg-white shadow-sm shrink-0">
                    {dueInfo.tone === "danger" ? (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    ) : (
                      <Wallet className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
                        {t("next_payment")}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${dueTextClasses[dueInfo.tone]}`} data-testid="next-payment-due">
                        {dueInfo.text}
                      </span>
                    </div>
                    <div className="font-display font-bold text-xl text-slate-900 mt-1 truncate">
                      {nextProgramName || t("payment_pending")}
                    </div>
                    {nextPay.due_date && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {t("due_date")}: <strong>{formatDMY(nextPay.due_date, lang)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-stretch md:items-end gap-2.5 shrink-0">
                  <div className="text-right">
                    <div className="font-display font-bold text-3xl text-slate-900 leading-none" data-testid="next-payment-amount">
                      $ {Number(nextPay.amount || 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-slate-500 tracking-wider uppercase">USD</div>
                  </div>
                  {nextEnr?.payment_link ? (
                    <a
                      href={nextEnr.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="next-payment-cta"
                      className="inline-flex items-center justify-center gap-1.5 bg-brand text-white px-4 py-2 rounded-md font-semibold text-sm hover:bg-brand/90 transition-colors shadow-sm whitespace-nowrap"
                    >
                      {t("pay_now")}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : nextEnr?.bank_account ? (
                    <div className="text-right text-xs" data-testid="next-payment-bank">
                      <div className="flex items-center gap-1 justify-end text-slate-600 mb-0.5">
                        <Landmark className="h-3 w-3" />
                        {t("bank_deposit")}
                      </div>
                      <div className="font-mono text-[11px] text-slate-800 bg-white/70 px-2 py-1 rounded border border-slate-200">
                        {bankLabelFor(nextEnr.bank_account)}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to="/dashboard/my-payments"
                      className="inline-flex items-center justify-center gap-1.5 bg-white text-slate-700 px-4 py-2 rounded-md font-medium text-sm border border-slate-300 hover:bg-slate-50 transition-colors shadow-sm whitespace-nowrap"
                      data-testid="next-payment-cta-fallback"
                    >
                      {t("view_payment_details")}
                    </Link>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* "You're all caught up" celebration when no pending payments */}
          {!nextPay && data.pay.length > 0 && (
            <Card className="p-5 mb-6 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200" data-testid="caught-up-widget">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-full bg-white shadow-sm shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <div className="font-display font-bold text-base text-slate-900">{t("all_caught_up")}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{t("no_pending_payments")}</div>
                </div>
              </div>
            </Card>
          )}

          <div className="mb-10">
            <StudentAccessCards />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("groups")}</div>
                <BookOpen className="h-4 w-4 text-slate-400" />
              </div>
              <div className="font-display text-4xl font-bold mt-3">{data.groups.length}</div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("attendance")}</div>
                <CalendarCheck className="h-4 w-4 text-slate-400" />
              </div>
              <div className="font-display text-4xl font-bold mt-3">{data.att.length}</div>
              <Link to="/dashboard/my-attendance" className="text-xs text-blue-600 hover:underline mt-2 inline-block">→ {t("my_attendance")}</Link>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{t("pending")}</div>
                <Wallet className="h-4 w-4 text-slate-400" />
              </div>
              <div className="font-display text-4xl font-bold mt-3">$ {totalPending.toFixed(2)}</div>
              <Link to="/dashboard/my-payments" className="text-xs text-blue-600 hover:underline mt-2 inline-block">→ {t("my_payments")}</Link>
            </Card>
          </div>

          <h3 className="font-display text-xl font-semibold mb-3">{t("my_groups")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {data.groups.map((g) => (
              <Card key={g.id} className="p-5">
                <h4 className="font-display text-base font-semibold">{g.name}</h4>
                <p className="text-xs text-slate-500 mt-1">
                  {sedesMap[g.sede_id]} · {programsMap[g.program_id]}
                </p>
                {g.schedule && <p className="text-xs text-slate-500 mt-1">{t("schedule")}: {g.schedule}</p>}
                <ClassCardActions group={g} student />
              </Card>
            ))}
          </div>

          <div className="space-y-12 mb-8">
            <LeaderboardCard />
            <PhotosOfWeek />
            <WallFeed limit={1} allLink="/dashboard/wall-board" />
          </div>

        </>
      )}
    </div>
  );
}
